/**
 * 孙子数据模型
 * @author jzs0914@126.com
 */
import { fabric } from 'sunzi-fabric';

/** 主题 */
export enum Theme {
  dark = 'dark',
  light = 'light'
};

/**
 * 加价
 * @price 价格
 * @virtualId 虚拟产品id
 */
export interface IncreasePrice {
  price: number;
  virtualId: number;
}

/**
 * 第三方api调用
 * @status 状态
 * @data 返回数据
 * @message 错误信息
 */
export interface ApiResponse<T> {
  status: boolean;
  data?: T;
  message?: string;
}

/**
 * 校验状态
 * @waiting 等待
 * @error 错误
 * @pass 校验通过
 */
export enum ValidatorStatus {
  waiting = 0,
  error = 1,
  pass = 2,
}

/**
 * 店铺信息
 * @name 店铺名称
 * @currency 基准币种
 * @language 语言
 */
export interface Shop {
  name: string;
  currency: string;
  currencySymbol: string;
  language: string;
  code: string;
  logo: string;
}

/**
 * 杯子操作模块
 * @id 模块ID
 * @key 模块唯一标示
 * @name 模块名称
 * @description 模块描述
 * @pasters 模块关联的贴纸数据
 * @optionText 模块选项描述文案
 * @pastersConnect 贴纸关联关系集合
 * @rules 模块规格
 * @options 选项数据
 */
export interface Module {
  id: number;
  name?: string;
  description?: string;
  variantText?: string;
  options?: Option[];
}

export interface ModuleState extends Module {
  optionId?: number;
  validator: {
    status: ValidatorStatus;
    message: React.ReactNode[];
  };
}

/**
 * 选项数据
 * @value 选项文案
 * @increaseAmount 加价金额
 */
export interface Option {
  id: number;
  value?: string;
  increaseAmount?: number;
}

/**
 * 配件关联
 * @id 关联ID
 * @pid 主体ID
 * @acid 配件一级分类ID
 * @left
 * @top
 * @angle
 * @width
 * @height
 * @leve
 * @scale
 * @paddingTop
 * @paddingBottom
 * @paddingLeft
 * @paddingRight
 */
export interface PastersConnect {
  id: number;
  pid: number;
  acid: number;
  left: number;
  top: number;
  angle: number;
  width: number;
  height: number;
  level: number;
  scale: number;
  paddingTop: number;
  paddingBottom: number;
  paddingLeft: number;
  paddingRight: number;
}

/**
 * 主体一级分类
 * @id 分类ID
 * @name 分类名称
 * @image 分类图标
 * @children 二级分类数据集
 */
export interface PasterCategory {
  id: number;
  name: string;
  image: string;
  activeImage: string;
  label: string;
  children: Paster2ndCategory[]
}

/**
 * 主体二级分类
 * @id 分类ID
 * @name 分类名称
 * @image 分类图标
 * @children 主体数据集
 */
export interface Paster2ndCategory {
  id: number;
  name: string;
  image?: string;
  children: Paster[]
}

/**
 * 主体
 * @id 主体ID
 * @name 主体名称
 * @image 主体图片
 * @cropImage 主体crop图
 * @connects 关联关系集
 * @children 关联的贴纸分类集
 */
export interface Paster {
  id: number;
  name: string;
  image: string;
  cropImage: string;
  children: Annex1stCategory[]
}

/**
 * 贴纸一级分类
 * @id 贴纸分类ID
 * @macId 关联ID
 * @mainId 主体ID
 * @name 分类名称
 * @children 贴纸一级分类集合
 */
export interface Annex1stCategory {
  id: number;
  macId: number;
  mainId: number;
  name: string;
  children: Annex2ndCategory[]
}

/**
 * 贴纸二级分类
 * @id 贴纸分类ID
 * @name 分类名称
 * @children 贴纸集合
 */
export interface Annex2ndCategory {
  id: number;
  name: string;
  children: Annex[];
}

/**
 * 配件
 * id 配件ID
 * name 配件名称
 * image 配件图片文件
 * thumbnail 配件缩略图
 */
export interface Annex {
  id: number;
  name?: string;
  image: string;
  thumbnail: string;
}

/**
 * 贴纸默认数据
 * @top 上
 * @left 左
 * @scale 缩放
 * @angle 角度
 * @fcid 一级分类id
 * @scid 二级分类id
 */
export interface PasterOption {
  id: number; // 主体id
  rid: number; // 默认数据id 唯一标示
  top: number;
  left: number;
  width: number;
  height: number;
  scale: number;
  angle: number;
  fcid: number;
  scid: number;
  name: string;
  image: string;
  cropImage: string;
  index: number;
  annexs: PasterAnnexOption[];
  _group?: fabric.Group;
}

export interface PasterAnnexOption {
  id: number;
  facid: number;
  sacid: number;
  name?: string;
  image: string;
  thumbnail: string;
  _connect?: PastersConnect;
}

/**
 * 直线刻字默认数据
 * @id
 * @top 上
 * @left 左
 * @value 默认值
 * @fontSize 默认字号
 * @font 默认字体
 */
export interface LineTextOption {
  id: number;
  top: number;
  left: number;
  value: string;
  fontSize: number;
  font: Font;
  fontColor: string;
  fontShadowColor: string;
  _value?: string;
  _text?: fabric.Text;
}

/**
 * 字体
 * @name 字体名字
 * @path 字体地址
 */
export interface Font {
  id: number;
  name: string;
  subsetUrl: string;
  url: string;
}

/**
 * 贴纸规则
 * @lockMovementX 锁定x轴移动
 * @lockMovementY 锁定x轴移动
 * @lockFlipX 锁定镜像
 * @lockEdit 锁定编辑
 * @lockRotation 锁定旋转
 * @lockScale 锁定缩放
 * @originBottom 底部基准
 */
export interface RulePaster {
  lockMovementX: boolean;
	lockMovementY: boolean;
	lockFlipX: boolean;
	lockEdit: boolean;
	lockRotation: boolean;
  lockScale: boolean;
  originBottom: boolean;
}

/**
 * 直线刻字规则
 * @lockMovementX 锁定x轴移动
 * @lockMovementY 锁定x轴移动
 * @lockEdit 锁定编辑
 * @fonts 可选字体
 * @fontSize 可选字号
 * @isRequire 是否是必填项
 * @maxLength 最大输入长度
 * @minLength 最小输入长度
 * @limitLeft 限制区域 左
 * @limitTop 限制区域 上
 * @limitWidth 限制区域 宽度
 * @limitHeight 限制区域 高度
 */
export interface RuleLineText {
  lockMovementX: boolean;
  lockMovementY: boolean;
  lockEdit: boolean;
  lockScale: boolean;
  lockRotation: boolean;
	fonts: Font[];
  fontSizes: number[];
  fontColors: string[];
  fontShadowColors?: string[];
  isRequire: boolean;
  supportWrap: boolean;
	maxLength?: number;
	minLength?: number;
	limitLeft?: number;
	limitTop?: number;
	limitWidth?: number;
	limitHeight?: number;
}

/**
 * 底图规则
 * @left 左
 * @top 上
 * @width 宽度
 * @height 高度
 */
export interface RuleBottomImage {
  left: number;
	top: number;
	width: number;
	height: number;
}


/**
 * ai图规则
 * @boundLeft 内部空间的左
 * @boundTop 内部空间的上
 * @boundWidth 内部空间的宽度
 * @boundHeight 内部空间的高度
 */
export interface RuleAiImage {
  boundLeft?: number;
	boundTop?: number;
	boundWidth?: number;
	boundHeight?: number;
}

/**
 * RGB
 */
export interface RGB {
  r: number;
  g: number;
  b: number;
}

/**
 * 图片数据
 * @source 原图
 * @cropper 裁剪图
 * @lowQuality 低分辨率
 * @cropperData 裁剪数据
 */
export interface ImageData {
  source: string;
  size: number;
  objectURL?: string;
  base64?: string;
  croppieData?: number[];
}
